import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Package, ArrowRight } from "lucide-react";

/**
 * Public "Claim your prize" page — matches the printed bag flyer
 * (thelocalloot.com/claim).
 *
 * Hunter types the code from inside the bag. Backend resolves it to a
 * drop_id. We then redirect to the logged-in claim form at /drops/:id,
 * which runs the existing auth + winner flow.
 */
export default function ClaimPage() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const clean = code.trim();
    if (!clean) {
      setError("Enter the code from inside the bag.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/claim/resolve", { claim_code: clean });
      toast.success(`Nice find! Finishing your claim for ${data.title || "your prize"}…`);
      // Branch by resource type — drops go to /drops/:id, loot quests go
      // to the loot-quest claim page which has its own auth-light flow
      // (chests can be claimed by entering a username if not signed in).
      if (data.kind === "loot_quest") {
        nav(`/loot-quest/claim?code=${encodeURIComponent(clean)}`);
      } else {
        nav(`/drops/${data.drop_id}?code=${encodeURIComponent(clean)}`);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Code didn't work — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-10 max-w-xl mx-auto">
      <div className="overline">You found it</div>
      <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
        Claim your <span style={{ color: "#D7A62A" }}>prize</span>
      </h1>
      <p className="text-[#F5F2E8]/70 mt-3">
        Type the code you found inside the bag. We'll verify it and walk you
        through uploading your selfie for the claim.
      </p>

      <form onSubmit={submit} className="mt-8 loot-card-flat p-6 space-y-4">
        <div>
          <label className="overline block mb-2" htmlFor="claim-code">Claim code</label>
          <input
            id="claim-code"
            className="loot-input font-mono uppercase tracking-widest text-lg"
            placeholder="E.G. LOOT-KPT-001"
            value={code}
            autoFocus
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setCode(e.target.value)}
            data-testid="claim-code-input"
          />
          <p className="mt-1.5 text-[11px] font-mono uppercase tracking-widest text-[#F5F2E8]/50">
            UPPER or lower — both work
          </p>
        </div>

        {error && (
          <div
            className="font-mono text-sm p-3 border"
            style={{ color: "#E53935", borderColor: "#E53935" }}
            data-testid="claim-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="btn-loot w-full inline-flex items-center justify-center gap-2"
          data-testid="claim-submit-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
          {loading ? "Verifying…" : "Find my prize"}
          {!loading && <ArrowRight size={14} />}
        </button>
      </form>

      <div className="mt-6 text-xs font-mono text-[#F5F2E8]/50 uppercase tracking-widest text-center">
        Share the moment · #THELOCALLOOT
      </div>
    </div>
  );
}
